/*
* This class is used to create an OrderProduct object that is to display the ProductJson data in TMS_ORDERS
*/

import { IOriginHub, ITransitLane, TmsAsset } from "./TmsAsset";
import { Attachment, TmsInventory, TripHistoryItem } from "./TmsInventory";
import { unitConverter } from "../../../helpers/unitConverter";
import { InventoryItem } from "../../../helpers/objectUtils";
import { BolsCollection } from "../firebaseConfig";
import { ProductDataCache } from "../../../helpers/productDataCache";

export interface IOrderProduct {
  
// Asset 
  AssetId: string;
  Name: string;
  Description: string;
  Category: string;
  Subcategory: string;
  Sku: string;
  Upc: string;
  Manufacturer: string;
  Model: string;
  Price: number | null;
  Cost: number | null;
  Weight: number | null;
  Height: number | null;
  Width: number | null;
  Length: number | null;
  OriginHub: IOriginHub;
  
// Inventory
  InventoryId: string;
  Barcode: string;
  SerialNumber: string;
  Status: string;
  Quantity: number;
  QtyType: string | null; // Quantity type: 'pieces', 'skid pieces', 'liters', 'gallons', 'linear foot', etc.
  TripHistory: TripHistoryItem[] | null;
  Attachments: Attachment[] | null;
  ServiceTime: number;
  Shyftcode: string;
  Comment: string;
  PrintQuantity: string;
  DistributionZone: string;
  Client: { BusinessSubUnit: string, BusinessUnit: string };
  TransitLane: ITransitLane;  
  LatestBolId: string;
  Bols: { [key: string]: boolean };
  BolAttachments: Attachment[];

// Custom
  Volume: number | null;
  ProductUpdateTrigger: number | null;
  IsManagedByAssets: boolean;
  IsUpdated: boolean;   
}

export class OrderProduct {
    AssetId = '';
    Name = '';
    Description = '';
    Category = '';
    Subcategory = '';
    Width = 0;
    Length = 0;
    Height = 0;
    Sku = '';
    Upc = '';
    Manufacturer = '';
    Model = '';
    Price;
    Cost;
    Weight = 0;
    OriginHub = { Name: '', Id: '' };
    TransitLane = { Name: '', Id: '' };
    InventoryId = '';
    Barcode = '';
    SerialNumber = '';
    LotNumber = 0;
    Status = '';
    Quantity = 0;
    QtyType: string | null = null; // Quantity type: 'pieces', 'skid pieces', 'liters', 'gallons', 'linear foot', etc.
    TripHistory;
    Attachments;
    ServiceTime = 0;
    Shyftcode = '';
    Volume = 0;
    Comment = '';
    PrintQuantity = ''; 
    ProductUpdateTrigger = 0;
    IsManagedByAssets;
    IsUpdated = false;
    DistributionZone = '';
    Client;
    LatestBolId = '';
    Bols = {};
    BolAttachments: Attachment[] = [];
    
    constructor(data?: Partial<IOrderProduct>) {
        if (data) {
          Object.keys(data).forEach(key => {
            if (key in this) {
              (this as any)[key] = (data as any)[key];
            }
          });
        }
    }

  /*
   * Populate BolAttachments from the BOL doc behind LatestBolId. Reads the doc
   * from `cache` when the caller prefetched it, otherwise fetches it directly.
   * Both branches of fetchProductData (asset-managed and inventory-only) resolve
   * BOLs identically, so the logic lives here rather than being duplicated.
   */
  private async applyBolAttachments(latestBolId: string, cache?: ProductDataCache): Promise<void> {
    if (!latestBolId) {
      return;
    }

    try {
      // A cached key present but null means the batch confirmed no such BOL doc.
      const bolData = cache?.bols?.has(latestBolId)
        ? cache.bols.get(latestBolId)
        : await BolsCollection.doc(latestBolId)
            .get()
            .then((bolDoc: any) => (bolDoc.exists ? bolDoc.data() : null));

      if (bolData?.Url) {
        this.BolAttachments = [{
          filename: bolData.FileName || `BOL_${latestBolId}.pdf`,
          type: bolData.FileType || 'application/pdf',
          url: bolData.Url,
          bolId: latestBolId,
        }];
      }
    } catch (error) {
      console.error('Error fetching BOL attachments:', error);
    }
  }

  /*
   * `cache` is optional: when a caller has already resolved the inventory/BOL docs
   * in batches (OrderTable's initial load does, via prefetchProductData), pass it
   * and this skips its own single-doc reads. Every other call site omits it and
   * reads fresh, which the order editor and the table's snapshot listeners rely on.
   */
  async fetchProductData(inventory: InventoryItem, orderId: string, selectedWeightUnit, selectedVolumeUnit, selectedLengthUnit, cache?: ProductDataCache): Promise<void> {
    
    const inventoryId = inventory.InventoryId || "";
    
    // To avoid the error when the old archived data has different format
    // TODO: need to check and update the process to handle multiple formats
    if(!inventoryId) {
      return;
    }

    const inventoryObj = new TmsInventory({Id: inventoryId})
    // A cached key present but null means the batch confirmed the doc is absent —
    // leave the defaults standing, which is what getFromFirestore does for a
    // missing doc. Only an id the batch never saw falls through to its own read.
    if (cache?.inventory?.has(inventoryId)) {
      inventoryObj.hydrate(cache.inventory.get(inventoryId));
    } else {
      await inventoryObj.getFromFirestore();
    }

    if (inventory.IsManagedByAssets) {    
      const assetId = inventoryObj.AssetId || "";    
      const assetObj = new TmsAsset({ Id: assetId });
      await assetObj.getFromFirestore();
      
      this.AssetId = assetId;
      this.Name = assetObj?.Name || "";
      this.Description = assetObj?.Description || "";
      this.Category = assetObj?.Category || "";
      this.Subcategory = assetObj?.Subcategory || "";
      this.Sku = assetObj?.Sku || "";
      this.Upc = assetObj?.Upc || "";
      this.Manufacturer = assetObj?.Manufacturer || "";
      this.Model = assetObj?.Model || "";
      this.Price = assetObj?.Price || 0;
      this.Cost = assetObj?.Cost || 0;
      this.OriginHub = assetObj?.OriginHub || { Name: '', Id: '' };

      this.Weight = unitConverter(
        selectedWeightUnit.dataSourceUnit,
        selectedWeightUnit.selected,
        assetObj?.Weight || 0
      );
      
      this.Volume = unitConverter(
        selectedVolumeUnit.dataSourceUnit,
        selectedVolumeUnit.selected,
        assetObj ? assetObj?.Height * assetObj?.Width * assetObj?.Length : 0
      );
      this.Width = unitConverter(
        selectedLengthUnit.dataSourceUnit,
        selectedLengthUnit.selected,
        assetObj?.Width || 0
      );
      this.Length = unitConverter(
        selectedLengthUnit.dataSourceUnit,
        selectedLengthUnit.selected,
        assetObj?.Length || 0
      );
      this.Height = unitConverter(
        selectedLengthUnit.dataSourceUnit,
        selectedLengthUnit.selected,
        assetObj?.Height || 0
      );

      // Resolve the quantity/type: prefer the order line's own value, else the
      // inventory doc's (dispatch/optimizer orders store a thin ProductJson line with
      // no Quantity — it lives on the TMS_INVENTORY doc). The asset branch previously
      // left Quantity at its 0 default, corrupting per-item qty and order totals.
      this.Quantity = (inventory as any)?.Quantity ?? inventoryObj?.Quantity ?? 1;
      this.QtyType = (inventory as any)?.QtyType ?? inventoryObj?.QtyType ?? null;

      // Asset-managed products still get BOL info from inventory
      this.LatestBolId = inventoryObj?.LatestBolId;
      this.Bols = inventoryObj?.Bols;

      await this.applyBolAttachments(inventoryObj.LatestBolId, cache);
    } else {
      this.Name = inventoryObj?.Name;
      this.Description = inventoryObj?.Description;
      this.Category = inventoryObj?.Category;
      this.Subcategory = inventoryObj?.Subcategory;
      this.Sku = inventoryObj?.Sku;
      this.Upc = inventoryObj?.Upc;
      this.Manufacturer = inventoryObj?.Manufacturer;
      this.Model = inventoryObj?.Model;
      this.Price = inventoryObj?.Price;
      this.Cost = inventoryObj?.Cost;
      this.OriginHub = inventoryObj?.OriginHub;
      
      this.Weight = unitConverter(
        selectedWeightUnit.dataSourceUnit,
        selectedWeightUnit.selected,
        inventoryObj?.Weight
      );

      this.Volume = unitConverter(
        selectedVolumeUnit.dataSourceUnit,
        selectedVolumeUnit.selected,
        inventoryObj?.Volume
      );

      // Determine length unit based on volume unit (if volume is ft3, length is ft, otherwise m)
      const lengthUnit = selectedLengthUnit?.selected || 'm';
      
      // Convert dimensions from meters (dataSourceUnit) to selected length unit for display
      this.Width = unitConverter(selectedLengthUnit.dataSourceUnit, lengthUnit, inventoryObj?.Width || 0);
      this.Length = unitConverter(selectedLengthUnit.dataSourceUnit, lengthUnit, inventoryObj?.Length || 0);
      this.Height = unitConverter(selectedLengthUnit.dataSourceUnit, lengthUnit, inventoryObj?.Height || 0);

      this.LatestBolId = inventoryObj?.LatestBolId;
      this.Bols = inventoryObj?.Bols;

      await this.applyBolAttachments(inventoryObj.LatestBolId, cache);
    }

    this.InventoryId = inventoryObj.Id || "";
    this.DistributionZone = inventoryObj?.DistributionZone;
    this.Client = inventoryObj?.Client;
    this.Barcode = inventoryObj?.Barcode;
    this.SerialNumber = inventoryObj?.SerialNumber;
    // Preserve the order line's quantity (how many units are on THIS order), not
    // the TMS_INVENTORY doc's stock quantity. Using inventoryObj.Quantity here
    // overwrote the order's line qty on enrichment, so opening the order editor
    // (which re-enriches ProductJson) changed the displayed/stored quantity.
    this.Quantity = (inventory as any)?.Quantity ?? inventoryObj?.Quantity ?? 1;
    this.QtyType = (inventory as any)?.QtyType ?? inventoryObj?.QtyType ?? null;

    const tripHistory = Array.isArray(inventoryObj?.TripHistory) ? inventoryObj?.TripHistory : 
    inventoryObj?.TripHistory[orderId] || [];
    this.TripHistory = tripHistory    
    this.Status = tripHistory?.[tripHistory.length - 1]?.Status || ''
    
    this.Attachments = inventoryObj?.Attachments;
    this.ServiceTime = inventoryObj?.ServiceTime;
    this.Shyftcode = inventoryObj?.Shyftcode;
    this.Comment = inventoryObj?.Comment;
    this.PrintQuantity = inventoryObj?.PrintQuantity;
    this.TransitLane = inventoryObj?.TransitLane;

    this.ProductUpdateTrigger = inventory.ProductUpdateTrigger || new Date().getTime();
    this.IsManagedByAssets = inventory.IsManagedByAssets || false;

  }
  
  getLegacyProductData(product: any, selectedWeightUnit, selectedVolumeUnit) {

    if (product) {
      Object.keys(product).forEach(key => {
        if (key in this) {
          (this as any)[key] = (product as any)[key];
        }
      });
    }

    this.Name = product?.Product || product?.Name || "";

    this.Volume = unitConverter(
      selectedVolumeUnit.dataSourceUnit,
      selectedVolumeUnit.selected,
      product?.Volume
    );

    this.Weight = unitConverter(
      selectedWeightUnit.dataSourceUnit,
      selectedWeightUnit.selected,
      product?.Weight
    );

    this.IsManagedByAssets = false;

    // TODO - Add more fields as needed

    this.IsUpdated = false;
  }
}